import { CookiesService } from 'src/app/cookies/cookies.service';
import { ChatService } from 'src/app/shared/services/chat.service';
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, ReplaySubject } from 'rxjs';
import { User } from '../models/user.model';
import { map } from 'rxjs/operators';
import { Service } from 'src/app/shared/services/service';
import { UserSubscription } from '../models/user-subscription.model';
import { UserAffiliate } from '../models/user-affiliate.model';
import { UtilsService } from '../../shared/services/utils.service';
import { SENTRY, SentryErrors } from '../../shared/interfaces/sentry.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends Service {

  currentUserSubject: BehaviorSubject<User>;
  subscription: ReplaySubject<UserSubscription> = new ReplaySubject<UserSubscription>(1);
  affiliate: ReplaySubject<UserAffiliate> = new ReplaySubject<UserAffiliate>(1);
  loginEvent: Subject<void> = new Subject<void>();
  logoutEvent: Subject<void> = new Subject<void>();
  rememberMe = true;

  constructor(
    http: HttpClient,
    @Inject(SENTRY) private sentry: SentryErrors,
    private chat: ChatService,
    private cookieService: CookiesService,
    private utils: UtilsService,
  ) {
    super(http);

    let storedUser = null;
    try {
      if (sessionStorage.getItem('currentUser')) {
        storedUser = JSON.parse(sessionStorage.getItem('currentUser'));
        this.rememberMe = false;
      } else {
        storedUser = JSON.parse(localStorage.getItem('currentUser'));
      }
    } catch (e) {
      sentry.captureException(e);
    }
    this.currentUserSubject = new BehaviorSubject<User>(storedUser);
  }

  public get currentUser(): User {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string, rememberMe: boolean): Observable<User> {
    return this.http
      .post<User>(this.url + '/snack/v1/login', { username, password, rememberMe })
      .pipe(
        map((user) => {
          this.autoLogin(user, rememberMe);
          return user;
        })
      );
  }

  autoLogin(user: User, rememberMe: boolean = null) {
    if (this.utils.isBrowser) {
      this.sentry.setUser(user);
    }
    if (!user) {
      this.logout(false).subscribe();
      return;
    }
    if (this.validateEmail(user.email)) {
      this.chat.setUserEmail(user?.email);
    }
    if (rememberMe !== null) {
      this.rememberMe = rememberMe;
    }
    // Store User in local storage to keep user logged in between page reloads
    if (this.rememberMe) {
      localStorage.setItem('currentUser', JSON.stringify(user));
    } else {
      // Store User in session storage to not keep user logged
      sessionStorage.setItem('currentUser', JSON.stringify(user));
    }
    this.setSubscription(user.subscription);
    this.setAffiliate(user.affiliate);
    this.currentUserSubject.next(user);
    this.loginEvent.next();
  }

  private validateEmail(email: string) {
    if (!email) {
      return false;
    }
    const expression = /\S+@\S+\.\S+/;
    return expression.test(email);
  }

  public setSubscription(subscription: UserSubscription) {
    subscription = subscription ?? new UserSubscription();
    this.subscription.next(subscription);
  }

  public setAffiliate(affiliate: UserAffiliate) {
    affiliate = affiliate ?? new UserAffiliate();
    this.affiliate.next(affiliate);
  }

  logout(callService: boolean = true): Observable<void> {
    if (callService) {
      return this.doLogout().pipe(
        map(_ => {
          this.clearClientAuth();
        })
      );
    } else {
      this.clearClientAuth();
      return new Observable<void>(subject => { subject.next(); });
    }
  }

  private clearClientAuth() {
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('currentUser');
    this.setSubscription(null);
    this.setAffiliate(null);
    this.currentUserSubject.next(null);
    this.logoutEvent.next();
  }

  private doLogout(): Observable<void> {
    return this.http.post<void>(this.url + '/snack/v1/logout', null);
  }

  signUp(username: string, email: string, password: string, repeatPassword: string): Observable<User> {
    const data = { username, email, password, repeatPassword };
    return this.http.post<User>(this.url + '/snack/v1/join', data).pipe(
      map((user) => {
        this.autoLogin(user, true);
        return user;
      })
    );
  }

  /**
   * Receives either username or email.
   */
  getNewPassword(user: string): Observable<void> {
    const data = { user };
    return this.http.post<void>(this.url + '/snack/v1/resetPassword', data);
  }

  isLoggedIn() {
    if (this.cookieService.get('displayName')) {
      return true;
    }
    return false;
    // if (this.currentUser == null) {
    //   return false;
    // }
    // return true;
  }

  resetPassword(password: string, token: string, user: string): Observable<void> {
    const data = { pass: password, key: token, login: user };
    return this.http.post<void>(this.url + '/snack/v1/setPassword', data);
  }
}
